import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  IconBell,
  IconBuildingHospital,
  IconCheck,
  IconClipboardList,
  IconExternalLink,
  IconLoader2,
  IconUsers,
} from '@tabler/icons-react';
import axios from '../../library/axios';
import { MySwal } from '../../library/swal';
import CpanelPageShell from '../../components/cpanel/CpanelPageShell';
import CpanelStatCard from '../../components/cpanel/CpanelStatCard';
import CpanelActivityChart from '../../components/cpanel/CpanelActivityChart';
import {
  formatNotificationDateTime,
  formatNotificationTime,
} from '../../helpers/cpanelNotifications';
import { CPANEL_FEED_REFRESH_EVENT } from '../../helpers/cpanelJobFeedback';
import { LogEventBadge } from '../../components/moleculs/cpanel/logs/logEventUtils';
import { getApiErrorMessage } from '../../helpers/apiErrorMessage';

const formatLogTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('id-ID', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const EMPTY_SUMMARY = {
  tenants: { total: 0, deleted: 0 },
  users: { central: 0, hospital: 0, total: 0 },
  activity: { total: 0, chart: { labels: [], counts: [] }, recent: [] },
  notifications: { unreadCount: 0, recent: [] },
};

const CpanelDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);

  const fetchSummary = useCallback(async (options = {}) => {
    const { silent = false } = options;
    if (!silent) setLoading(true);
    try {
      const { data } = await axios.get('/cpanel/dashboard/summary');
      setSummary({
        tenants: data?.tenants || EMPTY_SUMMARY.tenants,
        users: data?.users || EMPTY_SUMMARY.users,
        activity: data?.activity || EMPTY_SUMMARY.activity,
        notifications: data?.notifications || EMPTY_SUMMARY.notifications,
      });
    } catch (err) {
      if (!silent) {
        setSummary(EMPTY_SUMMARY);
        MySwal.fire({
          icon: 'error',
          title: 'Gagal memuat dashboard',
          text: getApiErrorMessage(err, 'Tidak dapat mengambil ringkasan sistem.'),
        });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    const onRefresh = () => fetchSummary({ silent: true });
    window.addEventListener(CPANEL_FEED_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(CPANEL_FEED_REFRESH_EVENT, onRefresh);
  }, [fetchSummary]);

  const handleMarkRead = async (id) => {
    try {
      await axios.patch(`/cpanel/notifications/${id}/mark-read`);
      await fetchSummary({ silent: true });
    } catch (err) {
      MySwal.fire({
        icon: 'error',
        title: 'Gagal',
        text: getApiErrorMessage(err, 'Tidak dapat menandai notifikasi.'),
      });
    }
  };

  const handleOpenNotification = async (item) => {
    if (!item.isRead) {
      try {
        await axios.patch(`/cpanel/notifications/${item.id}/mark-read`);
      } catch {
        /* lanjut navigasi */
      }
    }
    if (item.url) {
      navigate(item.url);
    }
  };

  const { tenants, users, activity, notifications } = summary;
  const statValue = (n) => (loading ? '…' : n);

  return (
    <CpanelPageShell title="Dashboard" subtitle="Ringkasan sistem">
      <div className="cpanel-dashboard-section-label text-muted text-uppercase fs-8 fw-bold ls-1 mb-4">
        Hospital
      </div>
      <div className="row g-5 g-xl-8 mb-10 cpanel-dashboard-stats-row">
        <div className="col-12 col-lg-6">
          <CpanelStatCard
            icon={IconBuildingHospital}
            label="Tenant"
            value={statValue(tenants.total)}
            hint={
              tenants.deleted > 0
                ? `${tenants.deleted} tenant dihapus sementara`
                : 'Tenant Rumah Sakit aktif'
            }
            variant="brand"
            linkTo="/cpanel/tenants"
          />
        </div>
        <div className="col-12 col-lg-6">
          <CpanelStatCard
            icon={IconUsers}
            label="User"
            value={statValue(users.total)}
            hint={
              loading ? null : (
                <span className="d-flex flex-column gap-1">
                  <span>{users.central} Akun Central</span>
                  <span>{users.hospital} Akun Rumah Sakit</span>
                </span>
              )
            }
            variant="success"
            linkTo="/cpanel/users"
          />
        </div>
      </div>

      <div className="cpanel-dashboard-section-label text-muted text-uppercase fs-8 fw-bold ls-1 mb-4">
        Activity
      </div>
      <div className="row g-5 g-xl-8 mb-10">
        <div className="col-12">
          <CpanelActivityChart
            labels={activity.chart?.labels || []}
            counts={activity.chart?.counts || []}
            total={activity.total}
            loading={loading}
          />
        </div>
      </div>

      <div className="row g-5 g-xl-8">
        <div className="col-xl-7">
          <div className="card cpanel-card cpanel-dashboard-panel h-100">
            <div className="card-header border-0 pt-6 align-items-center">
              <h3 className="card-title fw-bold text-gray-800 mb-0">Notifikasi terbaru</h3>
              <div className="card-toolbar gap-2">
                {notifications.unreadCount > 0 ? (
                  <span className="badge badge-light-primary">
                    {notifications.unreadCount} belum dibaca
                  </span>
                ) : null}
                <Link to="/cpanel/notifications/all" className="btn btn-sm btn-light-primary">
                  Lihat
                </Link>
              </div>
            </div>
            <div className="card-body pt-2">
              {loading && notifications.recent.length === 0 ? (
                <div className="cpanel-dashboard-empty">Memuat notifikasi...</div>
              ) : null}
              {!loading && notifications.recent.length === 0 ? (
                <div className="cpanel-dashboard-empty">
                  <IconBell size={36} stroke={1.25} className="cpanel-dashboard-empty__icon" />
                  <p className="mb-0">Tidak ada notifikasi</p>
                </div>
              ) : null}
              {notifications.recent.length > 0 ? (
                <div className="cpanel-notifications-page__list cpanel-dashboard-notifications">
                  {notifications.recent.map((item) => {
                    const inProgress =
                      item.type === 'job.started' || item.data?.status === 'running';
                    const iconVariant =
                      item.data?.icon === 'danger'
                        ? 'danger'
                        : item.data?.icon === 'progress' || inProgress
                          ? 'progress'
                          : 'success';

                    return (
                      <div
                        key={item.id}
                        className={`cpanel-notifications-page__item${!item.isRead ? ' cpanel-notifications-page__item--unread' : ''}${inProgress ? ' cpanel-notifications-page__item--progress' : ''}`}
                      >
                        <div className="cpanel-notifications-page__item-main">
                          <span
                            className={`cpanel-notifications__item-icon cpanel-notifications__item-icon--${iconVariant}`}
                            aria-hidden="true"
                          >
                            {iconVariant === 'progress' ? (
                              <IconLoader2
                                size={18}
                                stroke={1.75}
                                className="cpanel-notifications__spin"
                              />
                            ) : (
                              <IconBell size={18} stroke={1.75} />
                            )}
                          </span>
                          <div className="cpanel-notifications-page__item-body">
                            <div className="cpanel-notifications-page__item-title">{item.title}</div>
                            {item.body ? (
                              <div
                                className={`cpanel-notifications-page__item-text${item.data?.icon === 'danger' ? ' cpanel-notifications-page__item-text--error' : ''}`}
                              >
                                {item.body}
                              </div>
                            ) : null}
                            <div className="cpanel-notifications-page__item-meta">
                              <span title={formatNotificationDateTime(item.createdAt)}>
                                {formatNotificationTime(item.createdAt)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="cpanel-notifications-page__item-actions">
                          {!item.isRead ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-light-primary"
                              onClick={() => handleMarkRead(item.id)}
                            >
                              <IconCheck size={16} stroke={2} />
                            </button>
                          ) : null}
                          {item.url ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-light"
                              onClick={() => handleOpenNotification(item)}
                            >
                              <IconExternalLink size={16} stroke={2} />
                            </button>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="col-xl-5">
          <div className="card cpanel-card cpanel-dashboard-panel h-100">
            <div className="card-header border-0 pt-6 align-items-center">
              <h3 className="card-title fw-bold text-gray-800 mb-0">Log aktivitas terbaru</h3>
              <div className="card-toolbar">
                <Link to="/cpanel/logs" className="btn btn-sm btn-light-primary">
                  Lihat
                </Link>
              </div>
            </div>
            <div className="card-body pt-2">
              {loading && activity.recent.length === 0 ? (
                <div className="cpanel-dashboard-empty">Memuat log...</div>
              ) : null}
              {!loading && activity.recent.length === 0 ? (
                <div className="cpanel-dashboard-empty">
                  <IconClipboardList
                    size={36}
                    stroke={1.25}
                    className="cpanel-dashboard-empty__icon"
                  />
                  <p className="mb-0">Belum ada aktivitas</p>
                </div>
              ) : null}
              {activity.recent.length > 0 ? (
                <ul className="cpanel-dashboard-logs list-unstyled mb-0">
                  {activity.recent.map((log) => (
                    <li key={log.id} className="cpanel-dashboard-logs__item">
                      <div className="cpanel-dashboard-logs__head">
                        <LogEventBadge event={log.event} />
                        <span className="cpanel-dashboard-logs__time">
                          {formatLogTime(log.createdAt)}
                        </span>
                      </div>
                      <div className="cpanel-dashboard-logs__desc">{log.description}</div>
                      {log.tenantSlug ? (
                        <div className="cpanel-dashboard-logs__meta text-muted fs-8">
                          Tenant: {log.tenantSlug}
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </CpanelPageShell>
  );
};

export default CpanelDashboardPage;
